import { supabase } from '../supabaseClient';
import type { ContactBalance, Loan, LoanFormData } from '../../types/loans';

export async function getLoans(options?: {
  showSettled?: boolean;
  search?: string;
}): Promise<Loan[]> {
  let query = supabase.from('loans').select('*').order('date', { ascending: false });

  if (!options?.showSettled) {
    query = query.eq('is_settled', false);
  }
  if (options?.search && options.search.trim()) {
    const term = options.search.trim();
    query = query.or(`contact_name.ilike.%${term}%,description.ilike.%${term}%`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data as Loan[]) || [];
}

export async function createLoan(data: LoanFormData): Promise<void> {
  // La RLS Supabase exige auth.uid() = user_id : on l'injecte explicitement.
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError || !user) {
    throw new Error('Vous devez être connecté pour enregistrer un prêt.');
  }

  const { error } = await supabase.from('loans').insert([{ ...data, user_id: user.id }]);
  if (error) throw error;
}

export async function updateLoan(id: string, updates: Partial<LoanFormData>): Promise<void> {
  const { error } = await supabase.from('loans').update(updates).eq('id', id);
  if (error) throw error;
}

export async function deleteLoan(id: string): Promise<void> {
  const { error } = await supabase.from('loans').delete().eq('id', id);
  if (error) throw error;
}

export async function settleLoan(id: string): Promise<void> {
  const { error } = await supabase.from('loans').update({ is_settled: true }).eq('id', id);
  if (error) throw error;
}

export async function getContactBalances(): Promise<ContactBalance[]> {
  const { data, error } = await supabase
    .from('loans')
    .select('contact_name, amount, is_lending, is_settled')
    .eq('is_settled', false)
    .order('contact_name');

  if (error) throw error;

  const balances = new Map<string, ContactBalance>();
  (data as any[] | null)?.forEach((loan) => {
    const contact = String(loan.contact_name ?? '').trim();
    if (!contact) return;

    if (!balances.has(contact)) {
      balances.set(contact, {
        contact_name: contact,
        total_lent: 0,
        total_borrowed: 0,
        balance: 0,
        transaction_count: 0,
      });
    }

    const b = balances.get(contact)!;
    b.transaction_count += 1;
    const amount = Number(loan.amount) || 0;
    if (loan.is_lending) b.total_lent += amount;
    else b.total_borrowed += amount;
    b.balance = b.total_lent - b.total_borrowed;
  });

  return Array.from(balances.values());
}

